<template>
  <div class="flex flex-col gap-5">
    <div class="flex flex-col items-start justify-between gap-x-16 gap-y-2 lg:flex-row">
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-4">
          <button class="grid place-items-center" @click="emit('back')">
            <RfBackIcon class="stroke-black-2! h-6! w-6!" />
          </button>
          <Transition mode="out-in">
            <RfBaseSkeleton
              v-if="skeleton"
              width="200"
              dynamic
              height="32"
              light
              srLabel="Loading prompt"
            />
            <h2 v-else class="text-heading-1 text-black-1 break-normal wrap-anywhere">
              {{ props.name || "Unnamed prompt" }}
            </h2>
          </Transition>
        </div>
        <span class="text-body text-grey-1 ml-10">{{ props.description }}</span>
      </div>
      <div class="inline-flex shrink-0 items-center justify-end gap-3 self-end">
        <Transition mode="out-in">
          <div
            v-if="skeleton"
            key="0"
            class="text-body text-grey-1 flex items-center justify-center"
          >
            <RfBaseSkeleton width="140" height="16" light />
          </div>
          <div
            v-else-if="props.type !== 'none'"
            key="1"
            class="text-body text-beige-50 flex items-center justify-center gap-1"
          >
            <RfPointIcon class="fill-beige-50 h-6! w-6!" />
            <component
              :is="customDevicesEnabled ? 'button' : 'span'"
              class="inline-flex items-center gap-1"
              @click="customDevicesEnabled ? emit('editCustomDevices') : undefined"
            >
              <span>{{ props.type }}</span>
              <RfEditIcon v-if="customDevicesEnabled" class="fill-beige-50 h-6 w-6" />
            </component>
          </div>
        </Transition>
        <Transition mode="out-in">
          <RfBaseSkeleton v-if="skeleton" width="120" height="40" light />
          <template v-else>
            <RfMenu
              v-if="!statusDisabled"
              sameWidth
              variant="outlined"
              minFit
              buttonType="white"
              class="[&_.rf-menu--icon-wrapper_>_span]:h-6! [&_.rf-menu--icon-wrapper_>_span]:w-6! [&_button.disabled]:pr-3"
              :disabled="
                props.disabled ||
                statusModel.type === SimplifyStatusesMapConstants.limited.type ||
                (props.isPushNotification &&
                  statusModel.type === SimplifyStatusesMapConstants.ended.type) ||
                !possibleStatuses?.length
              "
            >
              <template #activator>
                <span class="inline-flex items-center gap-1">
                  <span class="inline-flex h-6! w-6!">
                    <RfStatus class="m-auto" :status="statusModel.type" />
                  </span>
                  <span class="text-body text-grey-1 inline-block">
                    {{ StatusesConstants[statusModel.type].title }}
                  </span>
                </span>
              </template>
              <template #menu>
                <template v-for="status in possibleStatuses" :key="status.type">
                  <button
                    :disabled="props.disabledActions.includes(status.type)"
                    class="flex w-full items-center gap-3 px-4 py-2 first:pt-4! last:pb-4! disabled:cursor-not-allowed! disabled:opacity-50!"
                    @click="statusModel = status.type"
                  >
                    <RfStatus :status="status.type" />
                    {{ status.action }}
                  </button>
                </template>
              </template>
            </RfMenu>
          </template>
        </Transition>
        <RfMenu
          :disabled="props.disabled || !actionList?.length"
          preset="promptMenuEnd"
          buttonSize="base"
          buttonType="main"
          class="rf-submenu z-10 [&_.rf-button]:pr-2"
        >
          <template #activator>
            <span class="inline-flex items-center pr-3 pl-4">
              <span class="text-action-buttons text-grey-1"> Manage </span>
            </span>
          </template>
          <template #menu>
            <div class="list flex w-full flex-col items-start whitespace-nowrap">
              <template v-for="action in actionList">
                <template v-if="action?.if ?? true">
                  <RfSwitch
                    v-if="action.type === 'switch'"
                    :key="`switch-${action.title}`"
                    v-bind="action"
                  />
                  <button
                    v-else
                    :key="`button-${action.title}`"
                    class="text-action-buttons text-grey-1 flex w-full items-center gap-2 px-4 py-3 first:pt-6! last:pb-6!"
                    v-bind="action"
                  >
                    <component :is="action.icon" class="h-6! w-6!" :class="action.iconClass" />
                    {{ action.title }}
                  </button>
                </template>
              </template>
            </div>
          </template>
        </RfMenu>
      </div>
    </div>
    <div v-if="!hideAdditionalRow" class="flex flex-col justify-between gap-2 lg:flex-row">
      <RfDrillDownMetricsFilters
        v-model:filtersData="filtersData"
        :segments="props.segments"
        :appDevices="props.appDevices"
        :appLanguages="props.appLanguages"
        :appDeviceTypes="props.appDeviceTypes"
      />

      <TransitionGroup mode="out-in" class="place-self-end" tag="div">
        <RfDatePicker
          v-if="!skeleton"
          key="date"
          class="h-11 justify-self-end"
          :lockWithPreset="props.lockDatepickerPreset"
          :lockWithPresetMessage="props.lockDatepickerPresetMessage"
          :preset="props.defaultDate"
          :sinceStartDayRef="props.startDate"
          :min="dayjs().subtract(1, 'year')"
          :max="dayjs()"
          @input="v => emit('update:date', v)"
        />
        <RfBaseSkeleton v-else key="skeleton" width="383" height="44" light />
      </TransitionGroup>
    </div>
  </div>
</template>

<script setup>
import RfBackIcon from "@/components/icons/RfBackIcon.vue";
import RfEditIcon from "@/components/icons/RfEditIcon.vue";
import RfDatePicker from "@/components/inputs/RfDatePicker.vue";
import RfMenu from "@/components/menus/RfMenu.vue";
import StatusesConstants, {
  SimplifyStatusesMapConstants,
} from "@/utils/constants/PromptStatusesConstants";
import dayjs from "dayjs";
import { computed } from "vue";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import RfStatus from "../RfPrompts/RfStatus.vue";
import RfSwitch from "@/components/inputs/RfSwitch.vue";
import RfPointIcon from "@/components/icons/RfPointIcon.vue";
import RfDrillDownMetricsFilters from "@/blocks/RfDrillDownMetricsFilters.vue";

const statusModel = defineModel("status", { default: StatusesConstants.ended, type: Object });
const filtersData = defineModel("filtersData", {
  default: () => ({}),
  type: Object,
});

const emit = defineEmits(["back", "editCustomDevices", "update:date"]);

const props = defineProps({
  name: { type: String, default: "" },
  description: { type: String, default: "" },
  disabled: { type: [Boolean, String], default: true },
  type: { type: String, default: "none" },
  startDate: { type: String, default: null },
  defaultDate: { type: String, default: "this_week" },
  statusDisabled: { type: Boolean, default: false },
  skeleton: { type: Boolean, default: false },
  customDevicesEnabled: { type: Boolean, default: false },
  lockDatepickerPreset: { type: [String, Boolean], default: false },
  lockDatepickerPresetMessage: { type: String, default: null },
  isPushNotification: { type: Boolean, default: false },
  disabledActions: { type: Array, default: () => [] },
  possibleStatuses: { type: Array, default: () => [] },
  segments: { type: Array, default: () => [] },
  appDevices: { type: Array, default: () => [] },
  appLanguages: { type: Array, default: () => [] },
  appDeviceTypes: { type: Array, default: () => [] },
  hideAdditionalRow: { type: Boolean, default: false },
  actionList: { type: Array, default: () => [] },
});

const customDevicesEnabled = computed(() => !props.disabled && props.customDevicesEnabled);
</script>
